import { motion, useScroll, useTransform } from "framer-motion";
import { useState, useRef, useEffect, useCallback } from "react";
import medicalRecordsVideo from "../../assets/feature-scroll-content/medical-records.mp4";

/* ─── Floating Particle Node ──────────────────────────────────────────── */
const ParticleNode = ({ style }) => (
  <span aria-hidden className="services-particle absolute rounded-full" style={style} />
);

/* ─── Animated SVG Mesh Lines ─────────────────────────────────────────── */
const MeshLines = () => (
  <svg
    aria-hidden
    className="pointer-events-none absolute inset-0 h-full w-full opacity-30"
    xmlns="http://www.w3.org/2000/svg"
    preserveAspectRatio="xMidYMid slice">
    <defs>
      <linearGradient id="mg1" x1="0%" y1="0%" x2="100%" y2="100%">
        <stop offset="0%" stopColor="#3b82f6" stopOpacity="0.5" />
        <stop offset="100%" stopColor="#10b981" stopOpacity="0" />
      </linearGradient>
      <linearGradient id="mg2" x1="100%" y1="0%" x2="0%" y2="100%">
        <stop offset="0%" stopColor="#10b981" stopOpacity="0.4" />
        <stop offset="100%" stopColor="#3b82f6" stopOpacity="0" />
      </linearGradient>
    </defs>
    {[...Array(7)].map((_, i) => (
      <line
        key={`h${i}`}
        x1={`${-10 + i * 20}%`}
        y1="0%"
        x2={`${-10 + i * 20 + 30}%`}
        y2="100%"
        stroke="url(#mg1)"
        strokeWidth="0.6"
        className="services-mesh-line"
        style={{ animationDelay: `${i * 0.4}s` }}
      />
    ))}
    {[...Array(5)].map((_, i) => (
      <line
        key={`v${i}`}
        x1="0%"
        y1={`${-10 + i * 25}%`}
        x2="100%"
        y2={`${-10 + i * 25 + 20}%`}
        stroke="url(#mg2)"
        strokeWidth="0.5"
        className="services-mesh-line"
        style={{ animationDelay: `${i * 0.6 + 0.2}s` }}
      />
    ))}
  </svg>
);

/* ─── Records Preview ─────────────────────────────────────────────────── */
const RecordsPreview = () => {
  const [downloading, setDownloading] = useState(false);
  const [downloaded, setDownloaded] = useState(false);
  const [dlRow, setDlRow] = useState(null);

  // ── Cursor + modal state ──────────────────────────────────────────────
  const [cursorVisible,  setCursorVisible]  = useState(false);
  const [cursorPos,      setCursorPos]      = useState({ x: 24, y: 52 });
  const [cursorClicking, setCursorClicking] = useState(false);
  const [modalOpen,      setModalOpen]      = useState(false);
  const [modalPhase,     setModalPhase]     = useState("idle"); // idle | loading | done
  const [filesDone,      setFilesDone]      = useState([]);
  const [progress,       setProgress]       = useState(0);
  const [progressLabel,  setProgressLabel]  = useState("Preparing…");

  const dlBtnRef  = useRef(null);
  const wrapRef   = useRef(null);
  const loopTimer = useRef(null);

  const handleDownload = (i) => {
    if (downloading) return;
    setDlRow(i);
    setDownloading(true);
    setDownloaded(false);
    setTimeout(() => {
      setDownloading(false);
      setDownloaded(true);
      setTimeout(() => {
        setDownloaded(false);
        setDlRow(null);
      }, 1800);
    }, 1600);
  };

  const records = [
    { date: "May 30, 2026", type: "#2026A0237", docs: 1, links: 0 },
    { date: "May 2, 2026",  type: "#2026A0223", docs: 1, links: 1 },
    { date: "Apr. 4, 2026", type: "#2026A0160", docs: 1, links: 0 },
    { date: "Apr. 3, 2026", type: "#2026A0177", docs: 1, links: 1 },
  ];

  const fileSizes = ["124 KB", "98 KB", "87 KB", "110 KB"];

  // ── Auto-loop sequence ────────────────────────────────────────────────
  const runSequence = useCallback(() => {
    const ids = [];
    const t = (fn, ms) => { const id = setTimeout(fn, ms); ids.push(id); };

    // 1. Show cursor at top-left
    setCursorVisible(true);
    setCursorClicking(false);
    setCursorPos({ x: 24, y: 52 });

    // 2. Glide cursor to "Download All PDFs" button
    t(() => {
      if (dlBtnRef.current && wrapRef.current) {
        const b = dlBtnRef.current.getBoundingClientRect();
        const w = wrapRef.current.getBoundingClientRect();
        setCursorPos({
          x: b.left - w.left + b.width  / 2 - 8,
          y: b.top  - w.top  + b.height / 2 - 8,
        });
      }
    }, 600);

    // 3. Click press / release
    t(() => setCursorClicking(true),  1400);
    t(() => setCursorClicking(false), 1650);

    // 4. Open modal
    t(() => {
      setModalOpen(true);
      setModalPhase("loading");
      setFilesDone([]);
      setProgress(0);
      setProgressLabel("Preparing…");
    }, 1800);

    // 5. Hide cursor once modal opens
    t(() => setCursorVisible(false), 2000);

    // 6. Tick each file in with progress
    const steps = [
      { pct: 25,  label: "Fetching record 1…", file: 0 },
      { pct: 50,  label: "Fetching record 2…", file: 1 },
      { pct: 75,  label: "Fetching record 3…", file: 2 },
      { pct: 100, label: "Fetching record 4…", file: 3 },
    ];
    steps.forEach((s, i) => {
      t(() => {
        setProgress(s.pct);
        setProgressLabel(s.label);
        setFilesDone(prev => [...prev, s.file]);
      }, 2300 + i * 560);
    });

    // 7. Show done state
    const doneAt = 2300 + steps.length * 560 + 300;
    t(() => setModalPhase("done"), doneAt);

    // 8. Close modal + full reset
    const closeAt = doneAt + 2200;
    t(() => {
      setModalOpen(false);
      setModalPhase("idle");
      setFilesDone([]);
      setProgress(0);
      setCursorVisible(false);
    }, closeAt);

    // 9. Loop
    loopTimer.current = setTimeout(runSequence, closeAt + 1200);

    return () => ids.forEach(clearTimeout);
  }, []);

  useEffect(() => {
    const id = setTimeout(runSequence, 900);
    return () => {
      clearTimeout(id);
      clearTimeout(loopTimer.current);
    };
  }, [runSequence]);

  return (
    <div
      ref={wrapRef}
      className="flex h-full flex-col gap-0 overflow-hidden rounded-lg"
      style={{ position: "relative" }}>
      {/* ── All animations ── */}
      <style>{`
        @keyframes recSpin {
          from { transform: rotate(0deg); }
          to   { transform: rotate(360deg); }
        }
        .rec-spin { animation: recSpin 0.7s linear infinite; }

        @keyframes recProgressBar {
          0%   { width: 0%; }
          80%  { width: 90%; }
          100% { width: 100%; }
        }
        .rec-progress-bar { animation: recProgressBar 1.6s cubic-bezier(0.4,0,0.2,1) forwards; }

        @keyframes recSuccessPop {
          0%   { opacity: 0; transform: translateY(4px) scale(0.97); }
          100% { opacity: 1; transform: translateY(0) scale(1); }
        }
        .rec-success-pop { animation: recSuccessPop 0.35s ease forwards; }

        @keyframes recCreateBtn {
          0%, 100% { box-shadow: 0 0 0 0 rgba(79,70,229,0.4); }
          50%       { box-shadow: 0 0 0 4px rgba(79,70,229,0); }
        }
        .rec-create-btn { animation: recCreateBtn 2.5s ease-in-out infinite; }

        @keyframes recRowIn {
          0%   { opacity: 0; transform: translateY(6px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .records-row-in {
          opacity: 0;
          animation: recRowIn 0.35s ease forwards;
        }

        @keyframes recRefreshBtn {
          0%, 100% { transform: rotate(0deg); }
          50%       { transform: rotate(180deg); }
        }
        .rec-refresh-btn:hover svg { animation: recRefreshBtn 0.5s ease; }

       

        /* ── Modal ── */
        @keyframes recOverlayIn {
          from { opacity: 0; }
          to   { opacity: 1; }
        }
        @keyframes recModalIn {
          from { opacity: 0; transform: translateY(10px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        .rec-modal-overlay {
          position: absolute;
          inset: 0;
          background: rgba(0,0,0,0.38);
          border-radius: 10px;
          display: flex;
          align-items: center;
          justify-content: center;
          z-index: 40;
          animation: recOverlayIn 0.25s ease forwards;
        }
        .rec-modal-box {
          background: #fff;
          border: 0.5px solid rgba(0,0,0,0.12);
          border-radius: 12px;
          width: 240px;
          padding: 18px;
          animation: recModalIn 0.3s ease forwards;
        }

        /* ── File rows ── */
        @keyframes recFileIn {
          from { opacity: 0; transform: translateY(5px); }
          to   { opacity: 1; transform: translateY(0); }
        }
        .rec-file-in { animation: recFileIn 0.28s ease forwards; }

        @keyframes recCheckPop {
          0%  { opacity: 0; transform: scale(0.4); }
          60% { transform: scale(1.2); }
          100%{ opacity: 1; transform: scale(1); }
        }
        .rec-check-pop { animation: recCheckPop 0.3s ease forwards; }

        @keyframes recDonePop {
          from { opacity: 0; transform: translateY(4px) scale(0.97); }
          to   { opacity: 1; transform: translateY(0) scale(1); }
        }
        .rec-done-pop { animation: recDonePop 0.35s ease forwards; }
      `}</style>

      {/* ── Animated cursor ── */}
      {cursorVisible && (
        <div
          className={`pointer-events-none absolute z-50${cursorClicking ? " rec-cursor-clicking" : ""}`}
          style={{
            left: cursorPos.x,
            top: cursorPos.y,
            transition: "left 0.85s cubic-bezier(0.4,0,0.2,1), top 0.85s cubic-bezier(0.4,0,0.2,1)",
          }}>
          <svg width="22" height="22" viewBox="0 0 22 22" fill="none">
            <path
              d="M4 2L4 16L7.5 12.5L10.5 18L12.5 17L9.5 11L14 11Z"
              fill="#1e1b4b"
              stroke="#fff"
              strokeWidth="1.2"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      )}

      {/* ── Download modal overlay ── */}
      {modalOpen && (
        <div className="rec-modal-overlay">
          <div className="rec-modal-box">
            <div style={{ fontSize: "13px", fontWeight: 600, color: "#111", marginBottom: "3px" }}>
              Downloading medical records
            </div>
            <div style={{ fontSize: "11px", color: "#64748b", marginBottom: "12px" }}>
              Fluffy · {records.length} records found
            </div>

            {/* File list */}
            <div style={{ display: "flex", flexDirection: "column", marginBottom: "12px" }}>
              {records.map((r, i) => (
                <div
                  key={i}
                  className={filesDone.includes(i) ? "rec-file-in" : ""}
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: "8px",
                    padding: "5px 0",
                    borderBottom: i < records.length - 1 ? "0.5px solid rgba(0,0,0,0.06)" : "none",
                    opacity: filesDone.includes(i) ? 1 : 0.25,
                    transition: "opacity 0.2s",
                  }}>
                  <div
                    style={{
                      width: "22px",
                      height: "26px",
                      borderRadius: "3px",
                      background: "#ecfdf5",
                      border: "0.5px solid #6ee7b7",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      flexShrink: 0,
                    }}>
                    <svg width="10" height="12" viewBox="0 0 12 14" fill="none">
                      <path d="M1 1h7l3 3v9H1V1z" stroke="#065f46" strokeWidth="1" fill="none" />
                      <path d="M8 1v3h3" stroke="#065f46" strokeWidth="1" fill="none" />
                    </svg>
                  </div>
                  <div style={{ flex: 1 }}>
                    <div style={{ fontSize: "10px", fontWeight: 600, color: "#111" }}>
                      Record_{r.type.replace("#", "")}.pdf
                    </div>
                    <div style={{ fontSize: "9px", color: "#94a3b8" }}>{fileSizes[i]}</div>
                  </div>
                  {filesDone.includes(i) && (
                    <div
                      className="rec-check-pop"
                      style={{
                        width: "14px",
                        height: "14px",
                        borderRadius: "50%",
                        background: "#10b981",
                        display: "flex",
                        alignItems: "center",
                        justifyContent: "center",
                        flexShrink: 0,
                      }}>
                      <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                        <path
                          d="M1.5 4l2 2 3-3"
                          stroke="#fff"
                          strokeWidth="1.3"
                          strokeLinecap="round"
                          strokeLinejoin="round"
                        />
                      </svg>
                    </div>
                  )}
                </div>
              ))}
            </div>

            {/* Progress bar */}
            {modalPhase === "loading" && (
              <>
                <div
                  style={{
                    height: "4px",
                    background: "#f1f5f9",
                    borderRadius: "2px",
                    overflow: "hidden",
                    marginBottom: "6px",
                  }}>
                  <div
                    style={{
                      height: "100%",
                      background: "#10b981",
                      borderRadius: "2px",
                      width: `${progress}%`,
                      transition: "width 0.45s ease",
                    }}
                  />
                </div>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    fontSize: "10px",
                    color: "#64748b",
                  }}>
                  <span>{progressLabel}</span>
                  <span>{progress}%</span>
                </div>
              </>
            )}

            {/* Done banner */}
            {modalPhase === "done" && (
              <div
                className="rec-done-pop"
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: "6px",
                  background: "#ecfdf5",
                  border: "0.5px solid #6ee7b7",
                  borderRadius: "6px",
                  padding: "7px 10px",
                }}>
                <svg width="13" height="13" viewBox="0 0 13 13" fill="none">
                  <circle cx="6.5" cy="6.5" r="6" fill="#10b981" />
                  <path
                    d="M3.5 6.5l2 2 4-4"
                    stroke="#fff"
                    strokeWidth="1.3"
                    strokeLinecap="round"
                    strokeLinejoin="round"
                  />
                </svg>
                <span style={{ fontSize: "11px", fontWeight: 600, color: "#065f46" }}>
                  All {records.length} records downloaded!
                </span>
              </div>
            )}
          </div>
        </div>
      )}

      {/* ── Pet info header ── */}
      <div className="mb-2 flex items-center gap-3 rounded-xl border border-slate-100 bg-slate-50 px-3 py-2.5">
        <div className="flex h-8 w-8 shrink-0 items-center justify-center overflow-hidden rounded-full border-2 border-white bg-slate-200 shadow-sm">
          <span className="text-sm">🐕</span>
        </div>
        <div className="min-w-0 flex-1">
          <div className="flex items-center gap-1.5">
            <span className="text-[12px] font-bold text-slate-800">Fluffy</span>
            <span className="rounded-full border border-slate-300 bg-white px-1.5 py-0 text-[9px] text-slate-500">
              Dog
            </span>
          </div>
          <div className="text-[9px] text-slate-400">Owner: Allen Edrish Chavez</div>
          {/* Breed / Age / Gender chips */}
          <div className="mt-1.5 flex gap-1.5">
            {[
              ["BREED", "Shih Tzu"],
              ["AGE", "—"],
              ["GENDER", "Male"],
            ].map(([label, val]) => (
              <div
                key={label}
                className="min-w-12 rounded border border-slate-200 bg-white px-2 py-1">
                <div className="text-[7px] font-bold tracking-wider text-slate-400 uppercase">
                  {label}
                </div>
                <div className="text-[9px] font-semibold text-slate-700">{val}</div>
              </div>
            ))}
          </div>
        </div>
        {/* QR */}
        <div className="flex shrink-0 flex-col items-center gap-0.5">
          <div className="flex h-8 w-8 items-center justify-center rounded border border-slate-200 bg-white p-0.5">
            <svg width="26" height="26" viewBox="0 0 26 26" fill="none">
              <rect x="1" y="1" width="9" height="9" rx="1" fill="#1e1b4b" />
              <rect x="3" y="3" width="5" height="5" fill="white" />
              <rect x="4" y="4" width="3" height="3" fill="#1e1b4b" />
              <rect x="16" y="1" width="9" height="9" rx="1" fill="#1e1b4b" />
              <rect x="18" y="3" width="5" height="5" fill="white" />
              <rect x="19" y="4" width="3" height="3" fill="#1e1b4b" />
              <rect x="1" y="16" width="9" height="9" rx="1" fill="#1e1b4b" />
              <rect x="3" y="18" width="5" height="5" fill="white" />
              <rect x="4" y="19" width="3" height="3" fill="#1e1b4b" />
              <rect x="12" y="1" width="2" height="2" fill="#1e1b4b" />
              <rect x="12" y="5" width="2" height="2" fill="#1e1b4b" />
              <rect x="16" y="12" width="2" height="2" fill="#1e1b4b" />
              <rect x="20" y="12" width="2" height="2" fill="#1e1b4b" />
              <rect x="24" y="12" width="2" height="2" fill="#1e1b4b" />
              <rect x="12" y="12" width="2" height="2" fill="#1e1b4b" />
              <rect x="12" y="16" width="2" height="2" fill="#1e1b4b" />
              <rect x="16" y="20" width="2" height="2" fill="#1e1b4b" />
              <rect x="20" y="16" width="2" height="2" fill="#1e1b4b" />
              <rect x="20" y="20" width="4" height="4" fill="#1e1b4b" />
              <rect x="12" y="20" width="2" height="2" fill="#1e1b4b" />
              <rect x="12" y="24" width="2" height="2" fill="#1e1b4b" />
              <rect x="24" y="16" width="2" height="2" fill="#1e1b4b" />
              <rect x="24" y="20" width="2" height="2" fill="#1e1b4b" />
            </svg>
          </div>
          <span className="text-[7px] text-slate-400">Pet QR</span>
        </div>
      </div>

      {/* ── Action buttons row ── */}
      <div className="mb-2 flex items-center gap-2">
        {/* Search */}
        <div className="flex flex-1 items-center gap-1.5 rounded-lg border border-slate-200 bg-white px-2.5 py-1.5">
          <svg width="10" height="10" viewBox="0 0 14 14" fill="none">
            <circle cx="6" cy="6" r="4.5" stroke="#94a3b8" strokeWidth="1.4" />
            <path d="M10 10l2 2" stroke="#94a3b8" strokeWidth="1.4" strokeLinecap="round" />
          </svg>
          <span className="truncate text-[9px] text-slate-400">Search record type, date…</span>
        </div>
        {/* Create Medical Record */}
        <button
          className="rec-create-btn flex shrink-0 items-center gap-1 rounded-lg px-2.5 py-1.5 text-[9px] font-semibold text-white"
          style={{ background: "#4f46e5" }}>
          <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
            <rect
              x="1"
              y="1"
              width="10"
              height="10"
              rx="1.5"
              stroke="white"
              strokeWidth="1"
              fill="none"
            />
            <path d="M6 3.5v5M3.5 6h5" stroke="white" strokeWidth="1.2" strokeLinecap="round" />
          </svg>
          Create Record
        </button>
        {/* Download All PDFs */}
        <button
          ref={dlBtnRef}
          onClick={() => handleDownload("all")}
          className="rec-dl-all-btn flex shrink-0 items-center gap-1 rounded-lg border px-2.5 py-1.5 text-[9px] font-semibold transition-all"
          style={{
            borderColor: "#10b981",
            color: downloading && dlRow === "all" ? "#fff" : "#10b981",
            background: downloading && dlRow === "all" ? "#10b981" : "white",
          }}>
          {downloading && dlRow === "all" ? (
            <svg className="rec-spin" width="10" height="10" viewBox="0 0 12 12" fill="none">
              <circle
                cx="6"
                cy="6"
                r="4.5"
                stroke="white"
                strokeWidth="1.5"
                strokeDasharray="14 8"
                strokeLinecap="round"
              />
            </svg>
          ) : downloaded && dlRow === "all" ? (
            <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
              <path
                d="M2.5 6.5l2.5 2.5 4.5-5"
                stroke="#10b981"
                strokeWidth="1.5"
                strokeLinecap="round"
                strokeLinejoin="round"
              />
            </svg>
          ) : (
            <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
              <rect
                x="1"
                y="1"
                width="10"
                height="10"
                rx="1.5"
                stroke="#10b981"
                strokeWidth="1"
                fill="none"
              />
              <path
                d="M6 3v4M4 5.5L6 7.5l2-2M3 9h6"
                stroke="#10b981"
                strokeWidth="1"
                strokeLinecap="round"
              />
            </svg>
          )}
          {downloading && dlRow === "all"
            ? "Downloading…"
            : downloaded && dlRow === "all"
              ? "Downloaded!"
              : "Download All PDFs"}
        </button>
        {/* Refresh */}
        <button
          className="rec-refresh-btn flex h-6 w-6 shrink-0 items-center justify-center rounded-lg"
          style={{ background: "#1e1b4b" }}>
          <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
            <path d="M10 6A4 4 0 112 6" stroke="white" strokeWidth="1.3" strokeLinecap="round" />
            <path
              d="M10 3v3h-3"
              stroke="white"
              strokeWidth="1.3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
        </button>
      </div>

      {/* ── Table header ── */}
      <div
        className="grid gap-0 rounded-t-lg px-3 py-2"
        style={{ gridTemplateColumns: "2fr 2fr 1.2fr 1fr", background: "#1e1b4b" }}>
        {["Record Date", "Record Type", "Entries", "Actions"].map((h, i) => (
          <span
            key={h}
            className={`flex items-center gap-1 text-[9px] font-bold tracking-wider text-white/90 uppercase ${i === 3 ? "justify-end" : ""}`}>
            {h}
            {i < 2 && (
              <svg width="8" height="8" viewBox="0 0 8 8" fill="none">
                <path
                  d="M2 3l2-2 2 2M2 5l2 2 2-2"
                  stroke="white"
                  strokeWidth="1"
                  strokeLinecap="round"
                  strokeOpacity="0.7"
                />
              </svg>
            )}
          </span>
        ))}
      </div>

      {/* ── Table rows ── */}
      <div className="flex flex-col divide-y divide-slate-100 overflow-hidden rounded-b-lg border border-slate-100">
        {records.map((r, i) => (
          <div
            key={i}
            className="records-row-in grid items-center bg-white px-3 py-2.5"
            style={{ gridTemplateColumns: "2fr 2fr 1.2fr 1fr", animationDelay: `${i * 0.18}s` }}>
            <span className="text-[10px] font-medium text-slate-700">{r.date}</span>

            <div>
              <span
                className="rounded-md px-2 py-0.5 text-[9px] font-semibold"
                style={{ background: "#ecfdf5", color: "#065f46", border: "1px solid #6ee7b7" }}>
                {r.type}
              </span>
            </div>

            <div className="flex items-center gap-1.5">
              <span className="flex items-center gap-0.5 text-[10px] text-slate-500">
                <svg width="10" height="10" viewBox="0 0 12 12" fill="none">
                  <path
                    d="M2 2h5l3 3v5a1 1 0 01-1 1H2a1 1 0 01-1-1V3a1 1 0 011-1z"
                    stroke="#94a3b8"
                    strokeWidth="1"
                    fill="none"
                  />
                  <path d="M7 2v3h3" stroke="#94a3b8" strokeWidth="1" fill="none" />
                </svg>
                {r.docs}
              </span>
              {r.links > 0 && (
                <span className="flex items-center gap-0.5 rounded-md bg-blue-50 px-1.5 py-0.5 text-[9px] font-medium text-blue-500">
                  <svg width="8" height="8" viewBox="0 0 10 10" fill="none">
                    <path
                      d="M4 6a2.5 2.5 0 003.5 0l1-1a2.5 2.5 0 00-3.5-3.5l-.5.5"
                      stroke="#3b82f6"
                      strokeWidth="1"
                      strokeLinecap="round"
                    />
                    <path
                      d="M6 4a2.5 2.5 0 00-3.5 0l-1 1a2.5 2.5 0 003.5 3.5l.5-.5"
                      stroke="#3b82f6"
                      strokeWidth="1"
                      strokeLinecap="round"
                    />
                  </svg>
                  {r.links}
                </span>
              )}
            </div>

            {/* Actions */}
            <div className="flex items-center justify-end gap-1.5">
              {/* View */}
              <button className="flex h-5 w-5 items-center justify-center rounded">
                <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                  <path
                    d="M1 6s2-4 5-4 5 4 5 4-2 4-5 4-5-4-5-4z"
                    stroke="#6366f1"
                    strokeWidth="1"
                    fill="none"
                  />
                  <circle cx="6" cy="6" r="1.5" stroke="#6366f1" strokeWidth="1" fill="none" />
                </svg>
              </button>
              {/* Edit */}
              <button className="flex h-5 w-5 items-center justify-center rounded">
                <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                  <path
                    d="M8.5 1.5l2 2-7 7H1.5v-2l7-7z"
                    stroke="#6366f1"
                    strokeWidth="1"
                    strokeLinejoin="round"
                    fill="none"
                  />
                </svg>
              </button>
              {/* Per-row Download */}
              <button
                onClick={() => handleDownload(i)}
                className="flex h-5 w-5 items-center justify-center rounded transition-all"
                style={{
                  background:
                    dlRow === i && downloading
                      ? "#ef4444"
                      : dlRow === i && downloaded
                        ? "#10b981"
                        : "transparent",
                }}>
                {dlRow === i && downloading ? (
                  <svg className="rec-spin" width="11" height="11" viewBox="0 0 12 12" fill="none">
                    <circle
                      cx="6"
                      cy="6"
                      r="4"
                      stroke="white"
                      strokeWidth="1.3"
                      strokeDasharray="10 6"
                      strokeLinecap="round"
                    />
                  </svg>
                ) : dlRow === i && downloaded ? (
                  <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                    <path
                      d="M2.5 6.5l2.5 2.5 4.5-5"
                      stroke="white"
                      strokeWidth="1.5"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                ) : (
                  <svg width="11" height="11" viewBox="0 0 12 12" fill="none">
                    <path
                      d="M6 2v5M3.5 5.5L6 8l2.5-2.5M2 10h8"
                      stroke="#ef4444"
                      strokeWidth="1.2"
                      strokeLinecap="round"
                      strokeLinejoin="round"
                    />
                  </svg>
                )}
              </button>
            </div>
          </div>
        ))}
      </div>

      {/* ── Download progress bar (shows during any download) ── */}
      {downloading && (
        <div className="mt-2 overflow-hidden rounded-full bg-slate-100" style={{ height: "3px" }}>
          <div
            className="rec-progress-bar h-full rounded-full"
            style={{ background: dlRow === "all" ? "#10b981" : "#ef4444" }}
          />
        </div>
      )}
      {downloaded && !downloading && (
        <div className="rec-success-pop mt-2 flex items-center gap-1.5 rounded-lg border border-emerald-100 bg-emerald-50 px-3 py-1.5">
          <svg width="12" height="12" viewBox="0 0 12 12" fill="none">
            <circle cx="6" cy="6" r="5" fill="#10b981" />
            <path
              d="M3.5 6.5l1.5 1.5 3.5-4"
              stroke="white"
              strokeWidth="1.3"
              strokeLinecap="round"
              strokeLinejoin="round"
            />
          </svg>
          <span className="text-[10px] font-semibold text-emerald-700">
            {dlRow === "all"
              ? "All PDFs downloaded successfully!"
              : `Record downloaded successfully!`}
          </span>
        </div>
      )}
    </div>
  );
};

/* ─── Messaging Preview ───────────────────────────────────────────────── */
const MessagingPreview = () => {
  const templates = [
    {
      label: "Confirmed",
      type: "Appointment Confirmation",
      contact: "Maria T.",
      time: "Just now",
      msgs: [
        { dir: "out", parts: [{ t: "Buddy's vet appt on " }, { t: "Dec 15", c: "val" }, { t: " confirmed. " }, { t: "PawCare Vet", c: "val" }, { t: ". Inquiries: " }, { t: "(02) 555-1234", c: "val" }, { t: "." }] },
        { dir: "in",  parts: [{ t: "Thanks! See you then" }] },
      ],
    },
    {
      label: "Tomorrow",
      type: "Day-Before Reminder",
      contact: "Jose R.",
      time: "6:00 PM",
      msgs: [
        { dir: "out", parts: [{ t: "Hi! " }, { t: "Max", c: "val" }, { t: "'s appt is " }, { t: "tomorrow", c: "val" }, { t: " (9AM–5PM). " }, { t: "PawCare Vet", c: "val" }, { t: ". Services: " }, { t: "Checkup + Vaccines", c: "val" }, { t: ". Call " }, { t: "(02) 555-1234", c: "val" }, { t: "." }] },
        { dir: "in",  parts: [{ t: "Got it! We'll be ready" }] },
      ],
    },
    {
      label: "Today",
      type: "Same-Day Reminder",
      contact: "Ana L.",
      time: "8:30 AM",
      msgs: [
        { dir: "out", parts: [{ t: "Hi! " }, { t: "Luna", c: "val" }, { t: "'s appt is " }, { t: "today", c: "val" }, { t: " (9AM–5PM). " }, { t: "PawCare Vet", c: "val" }, { t: ". Inquiries: " }, { t: "(02) 555-1234", c: "val" }, { t: "." }] },
        { dir: "in",  parts: [{ t: "On our way!" }] },
      ],
    },
    {
      label: "Missed",
      type: "Missed Appointment Alert",
      contact: "Ben C.",
      time: "9:00 AM",
      msgs: [
        { dir: "out", parts: [{ t: "Hi! " }, { t: "Mochi", c: "val" }, { t: "'s appt was missed. Open today (9AM–5PM). Call " }, { t: "(02) 555-1234", c: "val" }, { t: " to reschedule. " }, { t: "PawCare Vet", c: "val" }, { t: "." }] },
      ],
    },
  ];

  const typeColors = {
    "Appointment Confirmation": { bg: "#ecfdf5", color: "#065f46", border: "#6ee7b7" },
    "Day-Before Reminder":      { bg: "#eff6ff", color: "#1e40af", border: "#93c5fd" },
    "Same-Day Reminder":        { bg: "#fefce8", color: "#854d0e", border: "#fde047" },
    "Missed Appointment Alert":  { bg: "#fef2f2", color: "#991b1b", border: "#fca5a5" },
  };

  const phoneStyles = `
    .msg-phone { background:#fff; border:0.5px solid rgba(0,0,0,0.1); border-radius:20px; overflow:hidden; display:flex; flex-direction:column; width:100%; height:100%; }
    .msg-phone-bar { background:#f8fafc; padding:9px 12px 7px; display:flex; align-items:center; gap:7px; border-bottom:0.5px solid rgba(0,0,0,0.07); flex-shrink:0; }
    .msg-avatar { width:26px; height:26px; border-radius:50%; background:#B5D4F4; display:flex; align-items:center; justify-content:center; font-size:10px; font-weight:600; color:#0C447C; flex-shrink:0; font-family:inherit; }
    .msg-contact { font-size:11px; font-weight:600; color:#0f172a; font-family:inherit; }
    .msg-badge { font-size:9px; color:#64748b; margin-left:auto; background:#f1f5f9; border:0.5px solid rgba(0,0,0,0.08); border-radius:8px; padding:2px 6px; font-family:inherit; transition:all 0.3s ease; }
    .msg-type-bar { padding:6px 12px; display:flex; align-items:center; gap:5px; border-bottom:0.5px solid rgba(0,0,0,0.05); transition:background 0.3s ease; flex-shrink:0; }
    .msg-type-dot { width:5px; height:5px; border-radius:50%; flex-shrink:0; transition:background 0.3s ease; }
    .msg-type-label { font-size:9px; font-weight:700; letter-spacing:0.08em; text-transform:uppercase; font-family:inherit; transition:color 0.3s ease; }
    .msg-body { flex:1; padding:12px 12px 14px; display:flex; flex-direction:column; gap:6px; overflow:hidden; min-height:0; }
    .msg-ts { text-align:center; font-size:9px; color:#94a3b8; margin-bottom:2px; font-family:inherit; flex-shrink:0; }
    .msg-row { display:flex; flex-shrink:0; }
    .msg-row.out { justify-content:flex-end; }
    .msg-row.in { justify-content:flex-start; }
    .msg-bubble { max-width:84%; border-radius:13px; padding:6px 10px; font-size:10px; line-height:1.5; opacity:0; transform:translateY(6px); transition:opacity 0.3s ease, transform 0.3s ease; font-family:inherit; }
    .msg-bubble.show { opacity:1; transform:translateY(0); }
    .msg-bubble.out { background:#3b82f6; color:#fff; border-bottom-right-radius:3px; }
    .msg-bubble.in  { background:#f1f5f9; color:#334155; border-bottom-left-radius:3px; }
    .msg-bubble .val { font-weight:600; }
    .msg-bubble.out .val { color:#bfdbfe; }
    .msg-typing-row { display:flex; justify-content:flex-start; flex-shrink:0; }
    .msg-typing { background:#f1f5f9; border-radius:13px; border-bottom-left-radius:3px; padding:7px 11px; display:flex; gap:3px; align-items:center; opacity:0; transition:opacity 0.2s; }
    .msg-typing.show { opacity:1; }
    .msg-typing span { width:4px; height:4px; border-radius:50%; background:#94a3b8; display:inline-block; animation:msgBounce 1s infinite; }
    .msg-typing span:nth-child(2) { animation-delay:0.2s; }
    .msg-typing span:nth-child(3) { animation-delay:0.4s; }
    @keyframes msgBounce { 0%,60%,100%{transform:translateY(0)} 30%{transform:translateY(-4px)} }
  `;

  const bodyRef    = useRef(null);
  const typeBarRef = useRef(null);
  const typeDotRef = useRef(null);
  const typeLblRef = useRef(null);
  const currentIndex = useRef(0);
  const timeouts     = useRef([]);

  const [contact, setContact] = useState(templates[0].contact);
  const [label,   setLabel]   = useState(templates[0].label);

  const clearAllTimeouts = () => { timeouts.current.forEach(clearTimeout); timeouts.current = []; };
  const t = (fn, ms) => { const id = setTimeout(fn, ms); timeouts.current.push(id); };

  const clearBody = (tmpl) => {
    if (!bodyRef.current) return;
    bodyRef.current.innerHTML = `<div class="msg-ts">Today · ${tmpl.time}</div>`;
  };

  const updateTypeBar = (tmpl) => {
    const c = typeColors[tmpl.type];
    if (!typeBarRef.current || !typeDotRef.current || !typeLblRef.current) return;
    typeBarRef.current.style.background = c.bg;
    typeBarRef.current.style.borderBottomColor = c.border;
    typeDotRef.current.style.background = c.color;
    typeLblRef.current.style.color = c.color;
    typeLblRef.current.textContent = tmpl.type;
  };

  const appendBubble = (parts, dir) => {
    if (!bodyRef.current) return;
    const row = document.createElement("div");
    row.className = `msg-row ${dir}`;
    const bubble = document.createElement("div");
    bubble.className = `msg-bubble ${dir}`;
    parts.forEach((p) => {
      if (p.c) {
        const s = document.createElement("span");
        s.className = p.c;
        s.textContent = p.t;
        bubble.appendChild(s);
      } else {
        bubble.appendChild(document.createTextNode(p.t));
      }
    });
    row.appendChild(bubble);
    bodyRef.current.appendChild(row);
    requestAnimationFrame(() => bubble.classList.add("show"));
  };

  const appendTyping = () => {
    if (!bodyRef.current) return null;
    const row = document.createElement("div");
    row.className = "msg-typing-row";
    const dots = document.createElement("div");
    dots.className = "msg-typing";
    for (let i = 0; i < 3; i++) { const s = document.createElement("span"); dots.appendChild(s); }
    row.appendChild(dots);
    bodyRef.current.appendChild(row);
    requestAnimationFrame(() => dots.classList.add("show"));
    return { row, dots };
  };

  const runTemplate = (ti) => {
    const tmpl = templates[ti];
    currentIndex.current = ti;

    setContact(tmpl.contact);
    setLabel(tmpl.label);
    clearBody(tmpl);
    updateTypeBar(tmpl);

    let delay = 300;

    tmpl.msgs.forEach((m) => {
      if (m.dir === "out") {
        const d = delay;
        t(() => appendBubble(m.parts, "out"), d);
        delay += 950;
      } else {
        const d = delay;
        let typingRef = null;
        t(() => { typingRef = appendTyping(); }, d);
        delay += 850;
        const d2 = delay;
        t(() => {
          if (typingRef) {
            typingRef.dots.classList.remove("show");
            setTimeout(() => typingRef.row.remove(), 200);
          }
          appendBubble(m.parts, "in");
        }, d2);
        delay += 800;
      }
    });

    t(() => runTemplate((ti + 1) % templates.length), delay + 1800);
  };

  useEffect(() => {
    updateTypeBar(templates[0]);
    const id = setTimeout(() => runTemplate(0), 400);
    return () => { clearTimeout(id); clearAllTimeouts(); };
  }, []);

  const initColor = typeColors[templates[0].type];

  return (
    <>
      <style>{phoneStyles}</style>
      <div style={{ width: "100%", height: "100%", display: "flex", flexDirection: "column" }}>
        <div className="msg-phone" style={{ flex: 1 }}>

          {/* Contact bar */}
          <div className="msg-phone-bar">
            <div className="msg-avatar">
              {contact.split(" ").map((w) => w[0]).join("")}
            </div>
            <div className="msg-contact">{contact}</div>
            <div className="msg-badge">{label}</div>
          </div>

          {/* Type highlight bar */}
          <div
            ref={typeBarRef}
            className="msg-type-bar"
            style={{ background: initColor.bg, borderBottom: `0.5px solid ${initColor.border}` }}>
            <div
              ref={typeDotRef}
              className="msg-type-dot"
              style={{ background: initColor.color }}
            />
            <span
              ref={typeLblRef}
              className="msg-type-label"
              style={{ color: initColor.color }}>
              {templates[0].type}
            </span>
          </div>

          {/* Messages */}
          <div className="msg-body" ref={bodyRef}>
            <div className="msg-ts">Today · {templates[0].time}</div>
          </div>

        </div>
      </div>
    </>
  );
};

/* ─── Slide 1: Trend Engine ───────────────────────────────────────────── */
const Slide1 = () => {
  const barRef = useRef(null);
  const bars = [
    { label: "Oct", h: 52, color: "#c7d2fe" },
    { label: "Nov", h: 65, color: "#a5b4fc" },
    { label: "Dec", h: 44, color: "#c7d2fe" },
    { label: "Jan", h: 75, color: "#818cf8" },
    { label: "Feb", h: 60, color: "#a5b4fc" },
    { label: "Mar", h: 92, color: "#6366f1" },
  ];
  const maxH = Math.max(...bars.map(b => b.h));
  useEffect(() => {
    if (!barRef.current) return;
    barRef.current.innerHTML = "";
    bars.forEach(b => {
      const col = document.createElement("div");
      col.style.cssText = "flex:1;display:flex;flex-direction:column;align-items:center;gap:3px;height:100%";
      const inner = document.createElement("div");
      inner.style.cssText = "flex:1;display:flex;align-items:flex-end;width:100%";
      const bar = document.createElement("div");
      bar.style.cssText = `width:100%;border-radius:4px 4px 0 0;background:${b.color};height:${(b.h/maxH)*100}%`;
      inner.appendChild(bar);
      const lbl = document.createElement("span");
      lbl.style.cssText = "font-size:8px;color:#94a3b8";
      lbl.textContent = b.label;
      col.appendChild(inner);
      col.appendChild(lbl);
      barRef.current.appendChild(col);
    });
  }, []);
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={{ fontSize: 8, fontWeight: 700, color: "#94a3b8", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 8 }}>Trend Engine</div>
      <div style={{ display: "flex", alignItems: "flex-end", justifyContent: "space-between", marginBottom: 8 }}>
        <div>
          <div style={{ fontSize: 20, fontWeight: 800, color: "#1e293b", lineHeight: 1 }}>₱85,029</div>
          <div style={{ fontSize: 9, color: "#94a3b8", marginTop: 2 }}>Revenue this month</div>
        </div>
        <span style={{ background: "#f0fdf4", color: "#22c55e", fontSize: 9, fontWeight: 700, borderRadius: 6, padding: "2px 7px" }}>↑ 18.4%</span>
      </div>
      <div ref={barRef} style={{ display: "flex", alignItems: "flex-end", gap: 5, height: 68, marginBottom: 6 }} />
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 5 }}>
        {[["54","Services"],["₱1,574","Avg. bill"],["9","Pet Owners"]].map(([val,lbl]) => (
          <div key={lbl} style={{ background: "#f8fafc", borderRadius: 7, padding: "6px 8px", border: "1px solid #f1f5f9" }}>
            <div style={{ fontSize: 13, fontWeight: 700, color: "#1e293b" }}>{val}</div>
            <div style={{ fontSize: 8, color: "#94a3b8" }}>{lbl}</div>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ─── Slide 2: Appointment Sales Summary ──────────────────────────────── */
const Slide2 = () => {
  const areaRef = useRef(null);
  useEffect(() => {
    if (!areaRef.current) return;
    const pts26 = [0,3200,9800,7200,10200,4100,8900,3500,10600,3800,7200,4200,6800,13200,5800,7400,12800,4100,7900,3500,6800,2800,4100,1200,1800,900,600,2800,1200,800,400];
    const pts25 = [0,1200,4100,3200,5100,2200,4500,1800,5200,2100,3800,2200,3500,6200,3100,3800,6100,2200,3900,1900,3400,1600,2100,800,900,600,400,1400,700,500,300];
    const W=280, H=72, pad=2;
    const maxV = Math.max(...pts26, ...pts25);
    const xs = pts26.map((_, i) => pad + (i/(pts26.length-1))*(W-pad*2));
    const toY = v => pad + (1 - v/maxV)*(H-pad*2);
    const pathLine = pts => xs.map((x,i) => (i===0?"M":"L")+x.toFixed(1)+","+toY(pts[i]).toFixed(1)).join(" ");
    const pathArea = pts => pathLine(pts)+` L${xs[xs.length-1]},${H} L${xs[0]},${H} Z`;
    areaRef.current.innerHTML = `
      <defs>
        <linearGradient id="g26" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="#22c55e" stop-opacity="0.3"/>
          <stop offset="100%" stop-color="#22c55e" stop-opacity="0.02"/>
        </linearGradient>
        <linearGradient id="g25" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0%" stop-color="#22c55e" stop-opacity="0.12"/>
          <stop offset="100%" stop-color="#22c55e" stop-opacity="0"/>
        </linearGradient>
      </defs>
      ${[0.25,0.5,0.75,1].map(t=>`<line x1="0" y1="${H-t*H}" x2="${W}" y2="${H-t*H}" stroke="#f1f5f9" stroke-width="0.5"/>`).join("")}
      <path d="${pathArea(pts25)}" fill="url(#g25)"/>
      <path d="${pathLine(pts25)}" fill="none" stroke="#22c55e" stroke-width="1" stroke-opacity="0.4" stroke-linejoin="round"/>
      <path d="${pathArea(pts26)}" fill="url(#g26)"/>
      <path d="${pathLine(pts26)}" fill="none" stroke="#22c55e" stroke-width="1.5" stroke-linejoin="round"/>
    `;
  }, []);
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={{ fontSize: 8, fontWeight: 700, color: "#94a3b8", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 8 }}>Appointment Sales Summary</div>
      <div style={{ fontSize: 8, color: "#64748b", marginBottom: 7 }}>Total Sales: ₱85,029.00</div>
      <svg ref={areaRef} viewBox="0 0 280 72" width="100%" height="72" preserveAspectRatio="none" style={{ marginBottom: 6 }} />
      <div style={{ display: "flex", gap: 10, marginTop: 5 }}>
        {[["March 2025", true],["March 2026", false]].map(([lbl, faded]) => (
          <div key={lbl} style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <div style={{ height: 2, width: 14, borderRadius: 1, background: "#22c55e", opacity: faded ? 0.4 : 1 }} />
            <span style={{ fontSize: 8, color: "#94a3b8" }}>{lbl}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ─── Slide 3: Service Category Sales ────────────────────────────────── */
const Slide3 = () => {
  const donutRef = useRef(null);
  const legendRef = useRef(null);
  useEffect(() => {
    if (!donutRef.current || !legendRef.current) return;
    const segments = [
      { label: "Vaccination",     count: 19, color: "#3b82f6" },
      { label: "Surgery",         count: 13, color: "#ef4444" },
      { label: "Grooming",        count: 12, color: "#22c55e" },
      { label: "Consultation",    count:  4, color: "#f59e0b" },
      { label: "Defieas",         count:  2, color: "#a855f7" },
      { label: "Diagnostic",      count:  1, color: "#ec4899" },
      { label: "Pet Hotel",       count:  1, color: "#f97316" },
      { label: "Preventive Care", count:  1, color: "#6366f1" },
      { label: "Dental",          count:  1, color: "#06b6d4" },
    ];
    const total = segments.reduce((s,x) => s+x.count, 0);
    const r=34, cx=44, cy=44, stroke=11;
    const circ = 2*Math.PI*r;
    let offset = 0;
    let arcs = "";
    segments.forEach(seg => {
      const dash = (seg.count/total)*circ;
      arcs += `<circle cx="${cx}" cy="${cy}" r="${r}" fill="none" stroke="${seg.color}" stroke-width="${stroke}" stroke-dasharray="${dash.toFixed(2)} ${(circ-dash).toFixed(2)}" stroke-dashoffset="${(-offset).toFixed(2)}" transform="rotate(-90 ${cx} ${cy})"/>`;
      offset += dash;
    });
    donutRef.current.innerHTML = `
      <circle cx="${cx}" cy="${cy}" r="${r}" fill="none" stroke="#f1f5f9" stroke-width="${stroke}"/>
      ${arcs}
      <text x="${cx}" y="${cy-3}" text-anchor="middle" font-size="13" font-weight="800" fill="#1e293b">${total}</text>
      <text x="${cx}" y="${cy+9}" text-anchor="middle" font-size="7" fill="#94a3b8">Total</text>
    `;
    legendRef.current.innerHTML = segments.map(seg => `
      <div style="display:flex;align-items:center;gap:4px;">
        <div style="width:6px;height:6px;border-radius:2px;background:${seg.color};flex-shrink:0;"></div>
        <span style="font-size:8px;color:#475569;flex:1;">${seg.label}</span>
        <span style="font-size:8px;font-weight:600;color:#1e293b;">${seg.count}</span>
      </div>
    `).join("");
  }, []);
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={{ fontSize: 8, fontWeight: 700, color: "#94a3b8", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 8 }}>Service Category Sales</div>
      <div style={{ fontSize: 9, color: "#94a3b8", marginBottom: 8 }}>Rendered Services</div>
      <div style={{ display: "flex", alignItems: "center", gap: 10 }}>
        <svg ref={donutRef} width="88" height="88" viewBox="0 0 88 88" style={{ flexShrink: 0 }} />
        <div ref={legendRef} style={{ display: "flex", flexDirection: "column", gap: 2, flex: 1 }} />
      </div>
    </div>
  );
};

/* ─── Slide 4: Revenue vs Expenses ───────────────────────────────────── */
const Slide4 = () => {
  const chartRef = useRef(null);
  useEffect(() => {
    if (!chartRef.current) return;
    const days = [
      {rev:3200,exp:0},{rev:8200,exp:0},{rev:10500,exp:0},{rev:2800,exp:0},
      {rev:3500,exp:0},{rev:800,exp:0},{rev:7800,exp:0},{rev:0,exp:0},
      {rev:1200,exp:0},{rev:0,exp:0},{rev:0,exp:0},{rev:4200,exp:14000},
      {rev:4500,exp:0},{rev:11200,exp:0},{rev:1200,exp:1800},{rev:5800,exp:0},
      {rev:0,exp:0},{rev:14200,exp:0},{rev:3100,exp:0},{rev:0,exp:0},
      {rev:4800,exp:0},{rev:0,exp:0},{rev:3600,exp:0},{rev:0,exp:0},
      {rev:0,exp:0},{rev:0,exp:0},{rev:0,exp:0},{rev:4900,exp:0},
      {rev:0,exp:0},{rev:1200,exp:0},{rev:600,exp:0},
    ];
    const maxVal = Math.max(...days.flatMap(d => [d.rev, d.exp]));
    const W=280, H=72;
    const bw = (W-4)/days.length;
    let rects = [0.25,0.5,0.75,1].map(t=>`<line x1="0" y1="${H-t*H}" x2="${W}" y2="${H-t*H}" stroke="#f1f5f9" stroke-width="0.5"/>`).join("");
    days.forEach((d,i) => {
      const x = i*bw+1;
      const barW = bw*0.42;
      const revH = (d.rev/maxVal)*(H-2);
      const expH = (d.exp/maxVal)*(H-2);
      if (d.rev>0) rects += `<rect x="${x.toFixed(1)}" y="${(H-revH).toFixed(1)}" width="${barW.toFixed(1)}" height="${revH.toFixed(1)}" fill="#22c55e" rx="1"/>`;
      if (d.exp>0) rects += `<rect x="${(x+barW+0.5).toFixed(1)}" y="${(H-expH).toFixed(1)}" width="${barW.toFixed(1)}" height="${expH.toFixed(1)}" fill="#ef4444" rx="1"/>`;
    });
    chartRef.current.innerHTML = rects;
  }, []);
  return (
    <div style={{ display: "flex", flexDirection: "column", height: "100%" }}>
      <div style={{ fontSize: 8, fontWeight: 700, color: "#94a3b8", letterSpacing: "0.1em", textTransform: "uppercase", marginBottom: 8 }}>Revenue vs Expenses</div>
      <div style={{ fontSize: 8, color: "#64748b", marginBottom: 3 }}>Daily comparison of revenue and expenses</div>
      <div style={{ fontSize: 8, marginBottom: 7, display: "flex", gap: 6, flexWrap: "wrap" }}>
        <span style={{ color: "#22c55e", fontWeight: 700 }}>Revenue: ₱85,029</span>
        <span style={{ color: "#ef4444", fontWeight: 700 }}>Expenses: ₱17,000</span>
        <span style={{ color: "#3b82f6", fontWeight: 700 }}>Net Profit: ₱68,029</span>
      </div>
      <svg ref={chartRef} viewBox="0 0 280 72" width="100%" height="72" preserveAspectRatio="none" style={{ marginBottom: 5 }} />
      <div style={{ display: "flex", gap: 10, marginTop: 5 }}>
        {[["#22c55e","Revenue"],["#ef4444","Expenses"]].map(([color,lbl]) => (
          <div key={lbl} style={{ display: "flex", alignItems: "center", gap: 4 }}>
            <div style={{ width: 7, height: 7, background: color, borderRadius: 2 }} />
            <span style={{ fontSize: 8, color: "#94a3b8" }}>{lbl}</span>
          </div>
        ))}
      </div>
    </div>
  );
};

/* ─── InsightsPreview — auto carousel, no buttons ────────────────────── */
const SLIDES = [Slide1, Slide2, Slide3, Slide4];
const LABELS = ["Trend Engine", "Services", "Revenue", "Appointments"];
const INTERVAL = 3800;

const InsightsPreview = () => {
  const [active, setActive] = useState(0);
  const [phase, setPhase] = useState("in");

  useEffect(() => {
    const fade = setTimeout(() => {
      setPhase("out");
      const swap = setTimeout(() => {
        setActive(a => (a + 1) % SLIDES.length);
        setPhase("in");
      }, 350);
      return () => clearTimeout(swap);
    }, INTERVAL);
    return () => clearTimeout(fade);
  }, [active]);

  const SlideComponent = SLIDES[active];

  return (
    <div style={{
      fontFamily: "'DM Sans', 'Segoe UI', sans-serif",
      background: "linear-gradient(140deg, #eef2ff 0%, #dde7ff 100%)",
      borderRadius: 18,
      padding: "12px 12px 10px",
      width: "100%",
      boxSizing: "border-box",
    }}>
      <div style={{
        background: "#fff",
        borderRadius: 13,
        padding: "12px 13px",
        boxShadow: "0 2px 10px rgba(99,102,241,0.07)",
        minHeight: 220,
        display: "flex",
        flexDirection: "column",
      }}>
        <div style={{
          flex: 1,
          opacity: phase === "in" ? 1 : 0,
          transform: phase === "in" ? "translateY(0)" : "translateY(6px)",
          transition: "opacity 0.3s ease, transform 0.3s ease",
        }}>
          <SlideComponent />
        </div>
      </div>

      <div style={{ display: "flex", justifyContent: "center", gap: 5, marginTop: 9 }}>
        {SLIDES.map((_, i) => (
          <div key={i} style={{
            height: 5,
            width: active === i ? 18 : 5,
            borderRadius: 99,
            background: active === i ? "#6366f1" : "rgba(99,102,241,0.3)",
            transition: "all 0.35s ease",
          }} />
        ))}
      </div>
    </div>
  );
};

/* ─── Inventory Preview ───────────────────────────────────────────────── */
const InventoryPreview = () => {
  const rows = [
    {
      emoji: "💉",
      name: "Antibiotics",
      sub: "— • Antibiotics",
      tags: [],
      price: "₱1,000.00",
      cost: "₱20.00",
      stock: "15.00",
      status: "in-stock",
    },
    {
      emoji: "💊",
      name: "Antirabies",
      sub: "Antirabies • Vaccines",
      tags: [],
      price: "₱100.00",
      cost: "₱0.00",
      stock: "3.00",
      status: "low-stock",
    },
  ];

  const [lowOpen, setLowOpen] = useState(false);
  const [expOpen, setExpOpen] = useState(false);
  const [isCompact, setIsCompact] = useState(false);
  const [cursorPos, setCursorPos] = useState({ x: -100, y: -100 });
  const [cursorVisible, setCursorVisible] = useState(false);
  const lowAlertRef = useRef(null);
  const expAlertRef = useRef(null);
  const containerRef = useRef(null);
  const loopTimer = useRef(null);

  const lowStockItems = [
    { name: "Antirabies", min: "10.00", cur: "3.00" },
    { name: "Coat Shine 120ml", min: "15.00", cur: "5.00" },
    { name: "DIGYTON", min: "15.00", cur: "15.00" },
    { name: "Multivitamins+Lysine+Taurine (LC-VI...)", min: "5.00", cur: "2.00" },
  ];

  const expirationItems = [
    { name: "Ribavirin", exp: "Mar. 31, 2026", left: "0 days left", urgent: true },
    { name: "vial 2", exp: "Mar. 31, 2026", left: "0 days left", urgent: true },
    { name: "Ribavirin (Batch No. 4435345)", exp: "Mar. 31, 2026", left: "0 days left", urgent: true },
    { name: "Diaper male (X-Large)", exp: "Apr. 1, 2026", left: "1 day left", urgent: true },
    { name: "Cotrimoxazole Tablet", exp: "Apr. 1, 2026", left: "1 day left", urgent: true },
    { name: "NEOLIN 120ml", exp: "Apr. 1, 2026", left: "1 day left", urgent: true },
    { name: "Antitet ATS", exp: "Apr. 1, 2026", left: "1 day left", urgent: true },
    { name: "Mobility Bites Treats 17g", exp: "Apr. 1, 2026", left: "1 day left", urgent: true },
    { name: "Antibiotics", exp: "Apr. 13, 2026", left: "13 days left", urgent: false },
    { name: "Metapyrone", exp: "Apr. 24, 2026", left: "24 days left", urgent: false },
    { name: "Holistic Puppy", exp: "Apr. 26, 2026", left: "26 days left", urgent: false },
    { name: "Toposal", exp: "Apr. 27, 2026", left: "27 days left", urgent: false },
    { name: "25KG sack of Dog Food", exp: "Apr. 30, 2026", left: "30 days left", urgent: false },
  ];

  const getRelativeCenter = (ref) => {
    if (!ref.current || !containerRef.current) return { x: 0, y: 0 };
    const elRect = ref.current.getBoundingClientRect();
    const containerRect = containerRef.current.getBoundingClientRect();
    return {
      x: elRect.left - containerRect.left + elRect.width * 0.85,
      y: elRect.top - containerRect.top + elRect.height / 2,
    };
  };

  useEffect(() => {
    const updateCompact = () => setIsCompact(window.innerWidth < 640);
    updateCompact();
    window.addEventListener("resize", updateCompact);
    return () => window.removeEventListener("resize", updateCompact);
  }, []);

  useEffect(() => {
    const timeouts = [];
    const t = (fn, ms) => {
      const id = setTimeout(fn, ms);
      timeouts.push(id);
    };

    const runSequence = () => {
      setCursorVisible(true);

      // 1. Move cursor to Low Stock
      t(() => {
        const pos = getRelativeCenter(lowAlertRef);
        setCursorPos(pos);
      }, 400);

      // 2. Click — open Low Stock
      t(() => setLowOpen(true), 1100);

      // 3. Wait, then move cursor back to Low Stock to close
      t(() => {
        const pos = getRelativeCenter(lowAlertRef);
        setCursorPos(pos);
      }, 2800);

      // 4. Click — close Low Stock
      t(() => setLowOpen(false), 3500);

      // 5. Move cursor to Expiration
      t(() => {
        const pos = getRelativeCenter(expAlertRef);
        setCursorPos(pos);
      }, 4300);

      // 6. Click — open Expiration
      t(() => setExpOpen(true), 5000);

      // 7. Move cursor back to Expiration to close
      t(() => {
        const pos = getRelativeCenter(expAlertRef);
        setCursorPos(pos);
      }, 6500);

      // 8. Click — close Expiration
      t(() => setExpOpen(false), 7200);

      // 9. Loop
      loopTimer.current = setTimeout(runSequence, 8400);
    };

    const startId = setTimeout(runSequence, 600);
    timeouts.push(startId);

    return () => {
      timeouts.forEach(clearTimeout);
      clearTimeout(loopTimer.current);
    };
  }, [isCompact]);
  return (
    <div style={{ position: "relative" }} ref={containerRef}>
      {/* Animated Cursor */}
      {cursorVisible && (
        <div
          style={{
            position: "absolute",
            left: cursorPos.x - 9,
            top: cursorPos.y - 9,
            width: 18,
            height: 18,
            pointerEvents: "none",
            zIndex: 9999,
            transition: "left 0.6s cubic-bezier(.4,0,.2,1), top 0.6s cubic-bezier(.4,0,.2,1)",
          }}>
          <svg
            width="18"
            height="18"
            viewBox="0 0 18 18"
            fill="none"
            xmlns="http://www.w3.org/2000/svg">
            <path
              d="M2 2L2 14L5.5 10.5L8 16L10 15L7.5 9.5L12 9.5L2 2Z"
              fill="white"
              stroke="#333"
              strokeWidth="1.2"
              strokeLinejoin="round"
            />
          </svg>
        </div>
      )}

      {/* UI Preview */}
      <div
        style={{
          background: "#f0f6ff",
          borderRadius: 14,
          padding: isCompact ? "10px 10px 0 10px" : "14px 14px 0 14px",
          overflow: "visible",
          pointerEvents: "none",
        }}>
        <p
          style={{
            fontSize: 9,
            fontWeight: 600,
            color: "#94a3b8",
            letterSpacing: "1px",
            textTransform: "uppercase",
            margin: "0 0 10px",
          }}>
          Inventory Flow
        </p>

        {/* Low Stock Alert */}
        <div style={{ position: "relative", marginBottom: 6 }}>
          <div
            ref={lowAlertRef}
            onClick={() => setLowOpen((v) => !v)}
            style={{
              background: "#fffbeb",
              border: "1px solid #fde68a",
              borderLeft: "3px solid #f59e0b",
              borderRadius: "0 6px 6px 0",
              padding: "7px 12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              cursor: "pointer",
              userSelect: "none",
            }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: 10,
                fontWeight: 600,
                color: "#78350f",
              }}>
              <span style={{ fontSize: 11 }}>⚠️</span>
              Low Stock Alert
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span
                style={{
                  background: "#fef3c7",
                  color: "#92400e",
                  fontSize: 9,
                  fontWeight: 600,
                  padding: "1px 8px",
                  borderRadius: 20,
                }}>
                ● 4 items
              </span>
              <span
                style={{
                  fontSize: 10,
                  color: "#92400e",
                  display: "inline-block",
                  transition: "transform 0.3s",
                  transform: lowOpen ? "rotate(180deg)" : "rotate(0deg)",
                }}>
                ▾
              </span>
            </div>
          </div>

          {/* Floating dropdown — does NOT push layout */}
          {lowOpen && (
            <div
              style={{
                position: isCompact ? "relative" : "absolute",
                top: "100%",
                left: 0,
                right: 0,
                zIndex: 50,
                background: "#fff",
                border: "1px solid #fde68a",
                borderTop: "none",
                borderRadius: "0 0 8px 8px",
                padding: "10px 12px",
                display: "grid",
                gridTemplateColumns: isCompact ? "1fr" : "1fr 1fr",
                gap: 6,
                marginTop: isCompact ? 4 : 0,
                maxHeight: isCompact ? 230 : "none",
                overflowY: isCompact ? "auto" : "visible",
                boxShadow: "0 8px 24px rgba(0,0,0,0.10)",
              }}>
              {lowStockItems.map((item, i) => (
                <div
                  key={i}
                  style={{
                    background: "#fffbeb",
                    border: "1px solid #fde68a",
                    borderLeft: "3px solid #f59e0b",
                    borderRadius: "0 6px 6px 0",
                    padding: "7px 10px",
                  }}>
                  <div style={{ fontSize: 10, fontWeight: 600, color: "#78350f" }}>{item.name}</div>
                  <div style={{ fontSize: 9, color: "#92400e", marginTop: 2 }}>Min: {item.min}</div>
                  <div style={{ fontSize: 9, fontWeight: 700, color: "#b45309", marginTop: 2 }}>
                    Current: {item.cur}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>

        {/* Expiration Alert */}
        <div style={{ position: "relative", marginBottom: 6 }}>
          <div
            ref={expAlertRef}
            onClick={() => setExpOpen((v) => !v)}
            style={{
              background: "#fffbeb",
              border: "1px solid #fde68a",
              borderLeft: "3px solid #f59e0b",
              borderRadius: "0 6px 6px 0",
              padding: "7px 12px",
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              cursor: "pointer",
              userSelect: "none",
            }}>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 6,
                fontSize: 10,
                fontWeight: 600,
                color: "#78350f",
              }}>
              <span style={{ fontSize: 11 }}>🕐</span>
              Expiration Alert
            </div>
            <div style={{ display: "flex", alignItems: "center", gap: 6 }}>
              <span
                style={{
                  background: "#fef3c7",
                  color: "#92400e",
                  fontSize: 9,
                  fontWeight: 600,
                  padding: "1px 8px",
                  borderRadius: 20,
                }}>
                ● 13 items
              </span>
              <span
                style={{
                  fontSize: 10,
                  color: "#92400e",
                  display: "inline-block",
                  transition: "transform 0.3s",
                  transform: expOpen ? "rotate(180deg)" : "rotate(0deg)",
                }}>
                ▾
              </span>
            </div>
          </div>

          {/* Floating dropdown — does NOT push layout */}
          {expOpen && (
            <div
              style={{
                position: isCompact ? "relative" : "absolute",
                top: "100%",
                left: 0,
                right: 0,
                zIndex: 50,
                background: "#fff",
                border: "1px solid #fde68a",
                borderTop: "none",
                borderRadius: "0 0 8px 8px",
                padding: "10px 12px",
                display: "grid",
                gridTemplateColumns: isCompact
                  ? "repeat(auto-fit, minmax(125px, 1fr))"
                  : "1fr 1fr 1fr",
                gap: 6,
                marginTop: isCompact ? 4 : 0,
                maxHeight: isCompact ? 240 : "none",
                overflowY: isCompact ? "auto" : "visible",
                boxShadow: "0 8px 24px rgba(0,0,0,0.10)",
              }}>
              {expirationItems.map((item, i) => (
                <div
                  key={i}
                  style={{
                    background: "#fffbeb",
                    border: "1px solid #fde68a",
                    borderLeft: `3px solid ${item.urgent ? "#ef4444" : "#f59e0b"}`,
                    borderRadius: "0 6px 6px 0",
                    padding: "7px 10px",
                  }}>
                  <div
                    style={{
                      fontSize: 10,
                      fontWeight: 600,
                      color: "#78350f",
                      whiteSpace: "nowrap",
                      overflow: "hidden",
                      textOverflow: "ellipsis",
                    }}>
                    {item.name}
                  </div>
                  <div style={{ fontSize: 9, color: "#92400e", marginTop: 2 }}>
                    Expires: {item.exp}
                  </div>
                  <div
                    style={{
                      fontSize: 9,
                      fontWeight: 700,
                      color: item.urgent ? "#dc2626" : "#b45309",
                      marginTop: 2,
                    }}>
                    {item.left}
                  </div>
                </div>
              ))}
            </div>
          )}
        </div>
        {/* Expiration Dropdown */}
        

        {/* Tabs */}
        <div style={{ display: "flex", gap: 4, margin: "10px 0 8px", flexWrap: "wrap" }}>
          {[
            ["All Products", "1267", "#1e2a78", "#fff", "rgba(255,255,255,0.2)", "#fff"],
            ["Low Stock", "4", "transparent", "#64748b", "#fff7ed", "#c2410c"],
            ["Out of Stock", "487", "transparent", "#64748b", "#fff1f2", "#be123c"],
            ["Expired", "221", "transparent", "#64748b", "#f5f3ff", "#7c3aed"],
          ].map(([label, count, bg, color, cBg, cColor], i) => (
            <button
              key={i}
              style={{
                padding: "4px 10px",
                borderRadius: 6,
                fontSize: 10,
                fontWeight: 600,
                border: "none",
                cursor: "pointer",
                display: "flex",
                alignItems: "center",
                gap: 4,
                background: bg,
                color,
              }}>
              {label}
              <span
                style={{
                  fontSize: 9,
                  fontWeight: 700,
                  padding: "0px 5px",
                  borderRadius: 10,
                  background: cBg,
                  color: cColor,
                }}>
                {count}
              </span>
            </button>
          ))}
        </div>

        {/* Table */}
        <div
          style={{
            borderRadius: "6px 6px 0 0",
            overflowX: "auto",
            overflowY: "hidden",
            border: "1px solid #e2e8f0",
            borderBottom: "none",
          }}>
          <div
            style={{
              background: "#1e2a78",
              display: "grid",
              gridTemplateColumns: "2fr 1.6fr 0.9fr 0.8fr",
              minWidth: isCompact ? 430 : "auto",
              padding: "7px 12px",
              gap: 6,
            }}>
            {["Product ↕", "Price / Stock ↕", "Status", "Actions"].map((h, i) => (
              <div
                key={i}
                style={{
                  color: "#c7cfe8",
                  fontSize: 9,
                  fontWeight: 600,
                  textTransform: "uppercase",
                  letterSpacing: "0.5px",
                  display: "flex",
                  justifyContent: i === 3 ? "flex-end" : "flex-start",
                }}>
                {h}
              </div>
            ))}
          </div>

          {rows.map((r, i) => (
            <div
              key={i}
              style={{
                display: "grid",
                gridTemplateColumns: "2fr 1.6fr 0.9fr 0.8fr",
                minWidth: isCompact ? 430 : "auto",
                padding: "9px 12px",
                gap: 6,
                alignItems: "center",
                borderTop: "1px solid #f1f5f9",
                background: "#fff",
              }}>
              <div style={{ display: "flex", alignItems: "flex-start", gap: 7 }}>
                <div
                  style={{
                    width: 26,
                    height: 26,
                    background: "#eef0f8",
                    borderRadius: 6,
                    display: "flex",
                    alignItems: "center",
                    justifyContent: "center",
                    fontSize: 12,
                    flexShrink: 0,
                  }}>
                  {r.emoji}
                </div>
                <div style={{ minWidth: 0 }}>
                  <div style={{ fontSize: 10, fontWeight: 600, color: "#1a1a2e", lineHeight: 1.3 }}>
                    {r.name}
                  </div>
                  <div style={{ fontSize: 9, color: "#94a3b8", marginTop: 1 }}>{r.sub}</div>
                  {r.tags.length > 0 && (
                    <div style={{ display: "flex", gap: 3, marginTop: 3, flexWrap: "wrap" }}>
                      {r.tags.map((t, j) => (
                        <span
                          key={j}
                          style={{
                            fontSize: 8,
                            background: "#eff6ff",
                            color: "#1d4ed8",
                            border: "1px solid #bfdbfe",
                            padding: "1px 5px",
                            borderRadius: 20,
                          }}>
                          {t}
                        </span>
                      ))}
                    </div>
                  )}
                </div>
              </div>

              <div>
                <div style={{ fontSize: 11, fontWeight: 700, color: "#1a1a2e" }}>{r.price}</div>
                <div style={{ fontSize: 9, color: "#94a3b8", marginTop: 1 }}>
                  Cost: {r.cost} • Stock: {r.stock}
                </div>
              </div>

              <div>
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 3,
                    padding: "2px 7px",
                    borderRadius: 5,
                    fontSize: 9,
                    fontWeight: 600,
                    background: r.status === "in-stock" ? "#f0fdf4" : "#fffbeb",
                    color: r.status === "in-stock" ? "#15803d" : "#b45309",
                    border: `1px solid ${r.status === "in-stock" ? "#bbf7d0" : "#fde68a"}`,
                  }}>
                  <span
                    style={{
                      width: 4,
                      height: 4,
                      borderRadius: "50%",
                      background: r.status === "in-stock" ? "#22c55e" : "#f59e0b",
                      flexShrink: 0,
                    }}
                  />
                  {r.status === "in-stock" ? "In Stock" : "Low Stock"}
                </span>
                <div style={{ fontSize: 8, color: "#22c55e", fontWeight: 600, marginTop: 2 }}>
                  • ACTIVE
                </div>
              </div>

              <div
                style={{
                  display: "flex",
                  alignItems: "center",
                  gap: 3,
                  justifyContent: "flex-end",
                }}>
                {[
                  ["📦", "#ecfdf5"],
                  ["🕐", "#eff6ff"],
                  ["✏️", "#faf5ff"],
                  ["🗑️", "#fff1f2"],
                ].map(([icon, bg], j) => (
                  <button
                    key={j}
                    style={{
                      width: 20,
                      height: 20,
                      borderRadius: 5,
                      border: "none",
                      cursor: "pointer",
                      display: "flex",
                      alignItems: "center",
                      justifyContent: "center",
                      fontSize: 9,
                      background: bg,
                    }}>
                    {icon}
                  </button>
                ))}
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
};

/* ─── Main Component ──────────────────────────────────────────────────── */
const ServicesAbout = () => {
  const [typedText1, setTypedText1] = useState("");
  const [typedText2, setTypedText2] = useState("");
  const [hasStarted, setHasStarted] = useState(false);
  const sectionRef = useRef(null);
  const heroRef = useRef(null);

  const { scrollYProgress } = useScroll({
    target: heroRef,
    offset: ["start start", "end start"],
  });
  const orbY1 = useTransform(scrollYProgress, [0, 1], ["0%", "-30%"]);
  const orbY2 = useTransform(scrollYProgress, [0, 1], ["0%", "-18%"]);
  const orbOpacity = useTransform(scrollYProgress, [0, 0.6], [1, 0]);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting && !hasStarted) setHasStarted(true);
        });
      },
      { threshold: 0.3 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, [hasStarted]);

  useEffect(() => {
    if (!hasStarted) return;
    const str1 = "Everything Your ";
    const str2 = "Veterinary Clinic Needs.";
    let i1 = 0,
      i2 = 0,
      timeoutId;
    const type = () => {
      if (i1 < str1.length) {
        setTypedText1(str1.slice(0, i1 + 1));
        i1++;
        timeoutId = setTimeout(type, 50);
      } else if (i2 < str2.length) {
        setTypedText2(str2.slice(0, i2 + 1));
        i2++;
        timeoutId = setTimeout(type, 50);
      }
    };
    type();
    return () => clearTimeout(timeoutId);
  }, [hasStarted]);

  const particles = useRef(
    [...Array(22)].map((_, i) => ({
      id: i,
      size: Math.random() * 5 + 2,
      top: `${Math.random() * 90}%`,
      left: `${Math.random() * 100}%`,
      delay: `${Math.random() * 6}s`,
      duration: `${Math.random() * 8 + 8}s`,
      opacity: Math.random() * 0.5 + 0.15,
      color: i % 3 === 0 ? "#3b82f6" : i % 3 === 1 ? "#10b981" : "#9ecc48",
    }))
  ).current;

  const services = [
    {
      tagline: "Records",
      title: "1-Click Medical Records",
      description:
        "Fast. Organized. Effortless. Access and download a pet's full medical history from day 1 to the latest visit in seconds.",
      videoSrc: medicalRecordsVideo,
      accent: "#10b981",
      gridClass: "lg:col-span-2",
      previewLabel: "Record Sync",
      animationType: "records",
    },
    {
      tagline: "Automation",
      title: "Automated SMS Reminders",
      description:
        "Automated SMS Reminders sent to pet owners! Includes appointment confirmations, day-before reminders, and missed alerts.",
      videoSrc: medicalRecordsVideo,
      accent: "#3b82f6",
      gridClass: "lg:col-span-1",
      previewLabel: "Message Queue",
      animationType: "messaging",
    },
    {
      tagline: "Insights",
      title: "Business Analytics",
      description:
        "Smarter Decisions, faster. Instantly see your clinic's sales, expenses, and performance trends in detail to optimize growth.",
      videoSrc: medicalRecordsVideo,
      accent: "#6366f1",
      gridClass: "lg:col-span-1",
      previewLabel: "Trend Engine",
      animationType: "insights",
    },
    {
      tagline: "Management",
      title: "POS Inventory & Billing",
      description:
        "Inventory. Simplified. Manage sales in real time. Track product movement and keep transactions perfectly organized.",
      videoSrc: medicalRecordsVideo,
      accent: "#9ecc48",
      gridClass: "lg:col-span-2",
      previewLabel: "Inventory Flow",
      animationType: "management",
    },
  ];

  const renderPreviewByType = (service) => {
    if (service.animationType === "records") return <RecordsPreview />;
    if (service.animationType === "messaging") return <MessagingPreview />;
    if (service.animationType === "insights") return <InsightsPreview />;
    return <InventoryPreview />;
  };

  const cardVariants = {
    hidden: { opacity: 0, y: 70 },
    visible: {
      opacity: 1,
      y: 0,
      transition: { duration: 0.85, ease: [0.16, 1, 0.3, 1] },
    },
  };

  return (
    <section
      ref={heroRef}
      className="services-root relative z-10 overflow-hidden border-t border-[#93c5fd]/30 px-6 py-24 md:px-12 md:py-32">
      <style>{`
        .services-root {
          background: linear-gradient(160deg, #e0f2fe 0%, #f0fdf4 35%, #dbeafe 65%, #ecfdf5 100%);
        }

        @keyframes auroraA {
          0%   { transform: translate(-6%, -4%) scale(1);    opacity: 0.55; }
          33%  { transform: translate(8%,  5%)  scale(1.1);  opacity: 0.72; }
          66%  { transform: translate(-3%, 8%)  scale(0.95); opacity: 0.6;  }
          100% { transform: translate(-6%, -4%) scale(1);    opacity: 0.55; }
        }
        @keyframes auroraB {
          0%   { transform: translate(6%, -6%)  scale(1.05); opacity: 0.45; }
          40%  { transform: translate(-5%, 4%)  scale(1);    opacity: 0.68; }
          80%  { transform: translate(9%, 7%)   scale(1.08); opacity: 0.5;  }
          100% { transform: translate(6%, -6%)  scale(1.05); opacity: 0.45; }
        }
        @keyframes auroraC {
          0%   { transform: translate(0%, 0%)   scale(1);    opacity: 0.35; }
          50%  { transform: translate(-8%, 5%)  scale(1.12); opacity: 0.55; }
          100% { transform: translate(0%, 0%)   scale(1);    opacity: 0.35; }
        }
        .aurora-a { animation: auroraA 20s ease-in-out infinite; }
        .aurora-b { animation: auroraB 26s ease-in-out infinite; }
        .aurora-c { animation: auroraC 16s ease-in-out infinite; }

        @keyframes scanBeam {
          0%   { transform: translateY(-100%) skewY(-3deg); opacity: 0; }
          10%  { opacity: 0.18; }
          90%  { opacity: 0.18; }
          100% { transform: translateY(180%) skewY(-3deg); opacity: 0; }
        }
        .scan-beam { animation: scanBeam 6s cubic-bezier(0.4,0,0.6,1) infinite; animation-delay: 1.5s; }

        @keyframes floatUp {
          0%   { transform: translateY(0)     scale(1);    opacity: var(--po); }
          50%  { transform: translateY(-28px) scale(1.15); opacity: calc(var(--po) * 1.4); }
          100% { transform: translateY(0)     scale(1);    opacity: var(--po); }
        }
        .services-particle {
          animation: floatUp var(--dur) ease-in-out infinite;
          animation-delay: var(--delay);
          opacity: var(--po);
          width: var(--sz);
          height: var(--sz);
          background: var(--clr);
          filter: blur(1px);
          box-shadow: 0 0 8px 2px var(--clr);
        }

        @keyframes meshPulse {
          0%, 100% { opacity: 0.25; }
          50%       { opacity: 0.55; }
        }
        .services-mesh-line { animation: meshPulse 4s ease-in-out infinite; }

        @keyframes dotPan {
          0%   { transform: translate(0, 0); }
          100% { transform: translate(48px, 48px); }
        }
        .dot-grid { animation: dotPan 12s linear infinite; }

        .grain-overlay {
          background-image: url("data:image/svg+xml,%3Csvg viewBox='0 0 256 256' xmlns='http://www.w3.org/2000/svg'%3E%3Cfilter id='noise'%3E%3CfeTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='4' stitchTiles='stitch'/%3E%3C/filter%3E%3Crect width='100%25' height='100%25' filter='url(%23noise)' opacity='1'/%3E%3C/svg%3E");
          background-size: 128px 128px;
          opacity: 0.03;
          mix-blend-mode: multiply;
        }

        @keyframes bentoShimmer {
          0%   { background-position: -200% 0; }
          100% { background-position:  200% 0; }
        }
        .bento-shimmer {
          background: linear-gradient(100deg, transparent 0%, rgba(255,255,255,0.28) 45%, transparent 85%);
          background-size: 200% 100%;
          animation: bentoShimmer 4.5s linear infinite;
        }

        @keyframes cursorPulse {
          0%, 100% { transform: scale(1);   opacity: 1; }
          50%       { transform: scale(1.5); opacity: 0.6; }
        }
        .cursor-dot { animation: cursorPulse 2s ease-in-out infinite; }

        /* ── Records ── */
        @keyframes recordsRowIn {
          0%   { opacity: 0; transform: translateX(-10px); }
          100% { opacity: 1; transform: translateX(0); }
        }
        .records-row-in { opacity: 0; animation: recordsRowIn 0.45s ease forwards; }

        @keyframes recordsDlPulse {
          0%, 100% { box-shadow: 0 0 0 0 rgba(16,185,129,0.5); transform: scale(1); }
          50%       { box-shadow: 0 0 0 5px rgba(16,185,129,0); transform: scale(1.1); }
        }
        .records-dl-btn { animation: recordsDlPulse 2.2s ease-in-out infinite; }

        @keyframes recordsPulseGreen {
          0%, 100% { opacity: 1; transform: scale(1); }
          50%       { opacity: 0.5; transform: scale(1.4); }
        }
        .records-pulse { animation: recordsPulseGreen 1.4s ease-in-out infinite; }

        /* ── SMS ── */
        @keyframes smsBubbleIn {
          0%   { opacity: 0; transform: translateY(8px) scale(0.96); }
          100% { opacity: 1; transform: translateY(0)   scale(1); }
        }
        .sms-bubble-in { opacity: 0; animation: smsBubbleIn 0.4s ease forwards; }

        @keyframes smsPing {
          0%   { transform: scale(1);   opacity: 0.75; }
          100% { transform: scale(2.2); opacity: 0; }
        }
        .sms-ping { animation: smsPing 1.4s ease-out infinite; }

        /* ── Insights ── */
        @keyframes insightsBarGrow {
          0%   { height: 4px; }
          100% { height: var(--bar-h); }
        }
        .insights-bar { height: 4px; animation: insightsBarGrow 0.7s cubic-bezier(0.34,1.56,0.64,1) forwards; }

        @keyframes insightsKpiCount {
          0%   { opacity: 0; transform: translateY(6px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .insights-kpi { animation: insightsKpiCount 0.6s ease forwards; }

        @keyframes insightsTrend {
          0%   { opacity: 0; transform: translateX(8px); }
          100% { opacity: 1; transform: translateX(0); }
        }
        .insights-trend { animation: insightsTrend 0.5s ease 0.4s both; }

        @keyframes insightsChipIn {
          0%   { opacity: 0; transform: translateY(6px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .insights-chip { opacity: 0; animation: insightsChipIn 0.4s ease forwards; }

        /* ── Inventory ── */
        @keyframes invRowIn {
          0%   { opacity: 0; transform: translateY(8px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .inv-row-in { opacity: 0; animation: invRowIn 0.45s ease forwards; }

        @keyframes invBarFill {
          0%   { width: 0%; }
          100% { width: var(--bar-w); }
        }
        .inv-bar-fill { width: 0%; animation: invBarFill 0.9s cubic-bezier(0.34,1.2,0.64,1) forwards; }

        @keyframes invTotalBounce {
          0%   { opacity: 0; transform: translateY(6px); }
          60%  { transform: translateY(-3px); }
          100% { opacity: 1; transform: translateY(0); }
        }
        .inv-total { opacity: 0; animation: invTotalBounce 0.55s ease 1s forwards; }

        @media (prefers-reduced-motion: reduce) {
          .aurora-a, .aurora-b, .aurora-c, .scan-beam,
          .services-particle, .services-mesh-line, .dot-grid,
          .cursor-dot, .bento-shimmer,
          .records-row-in, .records-dl-btn, .records-pulse,
          .sms-bubble-in, .sms-ping,
          .insights-bar, .insights-kpi, .insights-trend, .insights-chip,
          .inv-row-in, .inv-bar-fill, .inv-total { animation: none; opacity: 1; }
        }
      `}</style>

      {/* ── Background Layer ─────────────────────────────────────── */}
      <div aria-hidden className="pointer-events-none absolute inset-0">
        <div className="grain-overlay absolute inset-0" />
        <div
          className="dot-grid absolute -inset-12 opacity-25"
          style={{
            backgroundImage:
              "radial-gradient(circle, rgba(100,116,139,0.22) 1.5px, transparent 1.5px)",
            backgroundSize: "48px 48px",
          }}
        />
        <div className="aurora-a absolute -top-28 -left-28 h-120 w-120 rounded-full bg-[radial-gradient(circle,rgba(59,130,246,0.22)_0%,rgba(59,130,246,0)_70%)]" />
        <div className="aurora-b absolute -right-28 -bottom-10 h-140 w-140 rounded-full bg-[radial-gradient(circle,rgba(16,185,129,0.2)_0%,rgba(16,185,129,0)_70%)]" />
        <div className="aurora-c absolute top-1/3 left-1/2 h-112 w-md -translate-x-1/2 rounded-full bg-[radial-gradient(circle,rgba(158,204,72,0.15)_0%,rgba(158,204,72,0)_72%)]" />
        <MeshLines />
        {particles.map((p) => (
          <ParticleNode
            key={p.id}
            style={{
              top: p.top,
              left: p.left,
              "--sz": `${p.size}px`,
              "--po": p.opacity,
              "--clr": p.color,
              "--dur": p.duration,
              "--delay": p.delay,
            }}
          />
        ))}
        <div className="scan-beam pointer-events-none absolute inset-x-0 top-0 h-[40%]">
          <div className="absolute inset-0 bg-linear-to-b from-transparent via-[rgba(59,130,246,0.06)] to-transparent" />
        </div>
        <div className="absolute inset-x-0 bottom-0 h-40 bg-linear-to-t from-[#ecfdf5]/60 to-transparent" />
      </div>

      {/* ── Content ─────────────────────────────────────────────── */}
      <div className="relative z-10 mx-auto w-full max-w-7xl">
        {/* Hero header */}
        <div ref={sectionRef} className="mb-28 flex flex-col items-center text-center">
          <motion.div
            initial={{ opacity: 0, scale: 0.85, y: 16 }}
            whileInView={{ opacity: 1, scale: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, ease: [0.16, 1, 0.3, 1] }}
            className="mb-7 flex items-center gap-2.5 rounded-full border border-[#93c5fd]/50 bg-white/80 px-5 py-2.5 shadow-md shadow-blue-100/60 backdrop-blur-md">
            <span className="cursor-dot h-2 w-2 rounded-full bg-[#10b981]" />
            <span className="text-xs font-bold tracking-[0.2em] text-[#5A7FA8] uppercase">
              All-In-One Solution
            </span>
          </motion.div>

          <motion.h2
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.7, delay: 0.1, ease: [0.16, 1, 0.3, 1] }}
            className="max-w-4xl text-5xl leading-none font-bold tracking-tight text-[#1A3A5C] md:text-6xl lg:text-7xl">
            <span className="inline-block min-h-[1.1em]">{typedText1}</span>
            <br />
            <span className="bg-linear-to-r from-blue-500 via-teal-400 to-[#9ecc48] bg-clip-text text-transparent">
              <span className="inline-block min-h-[1.1em]">
                {typedText2}
                {hasStarted && typedText2.length < 24 && (
                  <span className="ml-1 inline-block h-[0.85em] w-0.5 animate-pulse rounded-sm bg-blue-500 align-middle" />
                )}
              </span>
            </span>
          </motion.h2>

          <motion.p
            initial={{ opacity: 0, y: 16 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.6, delay: 0.25 }}
            className="mt-7 max-w-xl text-lg leading-relaxed text-[#4B6A8A]/80">
            One platform to automate, organize, and grow your veterinary practice — from the first
            appointment to lifetime care.
          </motion.p>

          <motion.div
            initial={{ scaleX: 0 }}
            whileInView={{ scaleX: 1 }}
            viewport={{ once: true }}
            transition={{ duration: 0.9, delay: 0.35, ease: [0.16, 1, 0.3, 1] }}
            className="mt-10 h-px w-32 origin-center rounded-full bg-linear-to-r from-transparent via-[#3b82f6]/60 to-transparent"
          />
        </div>

        {/* Service Cards */}
        <div className="grid grid-cols-1 gap-8 lg:grid-cols-3 lg:gap-9">
          {services.map((service, index) => (
            <motion.div
              key={index}
              variants={cardVariants}
              initial="hidden"
              whileInView="visible"
              viewport={{ once: true, margin: "-80px" }}
              className={`group relative overflow-hidden rounded-[2rem] border border-slate-200 bg-white p-6 shadow-[0_18px_45px_rgba(15,23,42,0.08)] transition-all duration-500 hover:-translate-y-1.5 hover:border-slate-300 hover:shadow-[0_28px_55px_rgba(15,23,42,0.14)] md:p-7 ${service.gridClass}`}>
              <div
                className="pointer-events-none absolute inset-0 rounded-[2rem] border border-transparent opacity-0 transition-opacity duration-500 group-hover:opacity-100"
                style={{ boxShadow: `inset 0 0 0 1px ${service.accent}55` }}
              />

              <div className="relative z-10">
                {/* Tag */}
                <div className="mb-4 inline-flex items-center gap-2 rounded-full border border-slate-200 bg-slate-50 px-3 py-1">
                  <span className="h-2 w-2 rounded-full" style={{ background: service.accent }} />
                  <span className="text-[10px] font-bold tracking-[0.16em] text-slate-500 uppercase">
                    {service.tagline}
                  </span>
                </div>

                <h3 className="mb-2 text-[1.85rem] leading-tight font-semibold tracking-tight text-slate-900 md:text-[2rem]">
                  {service.title}
                </h3>
                <p className="mb-5 max-w-2xl text-[0.98rem] leading-relaxed text-slate-600">
                  {service.description}
                </p>

                {/* Preview box — no label row, no footer */}
                <div className="relative overflow-hidden rounded-2xl border border-slate-200 bg-[linear-gradient(180deg,#f8fafc_0%,#f1f5f9_100%)] p-4 md:p-5">
                  <div className="bento-shimmer pointer-events-none absolute inset-0 opacity-70" />

                  {/* Preview label only (no LIVE badge, no footer) */}
                  <div className="relative mb-3 text-[11px] font-bold tracking-[0.16em] text-slate-400 uppercase">
                    {service.previewLabel}
                  </div>

                  {/* Content window — no center line, no flow-runner */}
                  <div
                    className="relative rounded-xl border border-slate-200 bg-white p-3 md:p-4"
                    style={service.animationType === "messaging" ? { height: "290px" } : {}}> 
                    <video
                      autoPlay
                      muted
                      loop
                      playsInline
                      src={service.videoSrc}
                      className="absolute inset-0 h-full w-full object-cover opacity-[0.03]"
                    />
                    <div
                      className={`relative z-10 ${service.animationType === "messaging" ? "h-full" : ""}`}>
                      {renderPreviewByType(service)}
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
};
export default ServicesAbout;
